import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Image as ImageIcon, Link2, FileText, Mic, Video } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { useClienteMedia } from '@/hooks/useClienteMedia';
import { useClienteLinks } from '@/hooks/useClienteLinks';
import { useWhatsAppMediaUrl } from '@/hooks/useWhatsAppMediaUrl';
import type { Mensagem } from '@/services/mensagens.service';

interface ProfileMediaProps {
  clienteId: string;
  /** Página larga (detalhe do cliente) → grelha com mais colunas. Default: painel estreito. */
  wide?: boolean;
}

// Extracts the first URL found in a message body, normalised for use as an href:
// a scheme is prepended for www-prefixed matches, and trailing sentence
// punctuation is stripped.
function firstUrl(text: string): string | null {
  const match = text.match(/(https?:\/\/|www\.)\S+/i);
  if (!match) return null;
  const raw = match[0].replace(/[.,;!?)\]"']+$/, '');
  return raw.startsWith('http') ? raw : `https://${raw}`;
}

function MediaThumb({ msg }: { msg: Mensagem }) {
  const { data: url } = useWhatsAppMediaUrl(
    msg.media_storage_path,
    msg.media_status === 'ready',
  );

  if (msg.tipo_media === 'image' && url) {
    return (
      <a href={url} target="_blank" rel="noreferrer">
        <img
          src={url}
          alt={msg.media_filename ?? `Imagem de ${msg.tipo_media ?? 'media'}`}
          className="aspect-square w-full rounded-md object-cover"
        />
      </a>
    );
  }

  const tileClassName =
    'flex aspect-square w-full flex-col items-center justify-center gap-1 rounded-md border border-border/50 bg-muted/30 p-2 text-muted-foreground hover:bg-muted/60 transition-colors';
  const inner = (
    <>
      <Video className="h-6 w-6" />
      <span className="text-[10px] truncate w-full text-center">
        {msg.media_filename ?? msg.tipo_media}
      </span>
    </>
  );
  return url ? (
    <a href={url} target="_blank" rel="noreferrer" className={tileClassName}>
      {inner}
    </a>
  ) : (
    <div className={tileClassName}>{inner}</div>
  );
}

function MediaRow({ msg, icon: Icon }: { msg: Mensagem; icon: typeof FileText }) {
  const { data: url } = useWhatsAppMediaUrl(
    msg.media_storage_path,
    msg.media_status === 'ready',
  );
  const rowClassName =
    'flex items-center gap-2 rounded-md border border-border/50 px-3 py-2 text-muted-foreground transition-colors';
  const inner = (
    <>
      <Icon className="h-4 w-4 shrink-0" />
      <span className="text-xs truncate">{msg.media_filename ?? msg.tipo_media}</span>
    </>
  );
  return url ? (
    <a href={url} target="_blank" rel="noreferrer" className={`${rowClassName} hover:bg-muted/50`}>
      {inner}
    </a>
  ) : (
    <div className={rowClassName}>{inner}</div>
  );
}

function EmptyState({ icon: Icon, label }: { icon: typeof ImageIcon; label: string }) {
  return (
    <div className="text-center py-6 text-muted-foreground">
      <Icon className="h-8 w-8 mx-auto mb-2 opacity-50" />
      <p className="text-xs">{label}</p>
    </div>
  );
}

export function ProfileMedia({ clienteId, wide = false }: ProfileMediaProps) {
  const { t } = useTranslation();
  const { data: media, isLoading: mediaLoading } = useClienteMedia(clienteId);
  const { data: links, isLoading: linksLoading } = useClienteLinks(clienteId);

  // Particiona a media por tipo, excluindo stickers.
  const { visuais, audios, arquivos } = useMemo(() => {
    const lista = (media ?? []).filter((m) => m.tipo_media !== 'sticker');
    return {
      visuais: lista.filter((m) => m.tipo_media === 'image' || m.tipo_media === 'video'),
      audios: lista.filter((m) => m.tipo_media === 'audio'),
      arquivos: lista.filter((m) => m.tipo_media === 'document'),
    };
  }, [media]);

  const linkRows = useMemo(
    () =>
      (links ?? [])
        .map((m) => ({ msg: m, url: firstUrl(m.conteudo ?? '') }))
        .filter((r): r is { msg: Mensagem; url: string } => r.url !== null),
    [links],
  );

  const gridClassName = wide
    ? 'grid grid-cols-4 md:grid-cols-6 gap-2'
    : 'grid grid-cols-3 gap-2';

  return (
    <div>
      <h4 className="text-sm font-medium text-foreground mb-3">{t('panel.mediaGalleryTitle')}</h4>
      <Tabs defaultValue="media">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="media" className="text-xs">
            <ImageIcon className="h-3.5 w-3.5 mr-1" /> {t('panel.mediaTab')}
          </TabsTrigger>
          <TabsTrigger value="audios" className="text-xs">
            <Mic className="h-3.5 w-3.5 mr-1" /> {t('panel.audiosTab')}
          </TabsTrigger>
          <TabsTrigger value="files" className="text-xs">
            <FileText className="h-3.5 w-3.5 mr-1" /> {t('panel.filesTab')}
          </TabsTrigger>
          <TabsTrigger value="links" className="text-xs">
            <Link2 className="h-3.5 w-3.5 mr-1" /> {t('panel.linksTab')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="media" className="mt-3">
          {mediaLoading ? (
            <div className={gridClassName}>
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="aspect-square w-full rounded-md" />
              ))}
            </div>
          ) : visuais.length === 0 ? (
            <EmptyState icon={ImageIcon} label={t('panel.noMedia')} />
          ) : (
            <div className={gridClassName}>
              {visuais.map((msg) => (
                <MediaThumb key={msg.id} msg={msg} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="audios" className="mt-3">
          {mediaLoading ? (
            <div className="space-y-2">
              {[1, 2].map((i) => (
                <Skeleton key={i} className="h-10 w-full rounded-md" />
              ))}
            </div>
          ) : audios.length === 0 ? (
            <EmptyState icon={Mic} label={t('panel.noAudios')} />
          ) : (
            <div className="space-y-2">
              {audios.map((msg) => (
                <MediaRow key={msg.id} msg={msg} icon={Mic} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="files" className="mt-3">
          {mediaLoading ? (
            <div className="space-y-2">
              {[1, 2].map((i) => (
                <Skeleton key={i} className="h-10 w-full rounded-md" />
              ))}
            </div>
          ) : arquivos.length === 0 ? (
            <EmptyState icon={FileText} label={t('panel.noFiles')} />
          ) : (
            <div className="space-y-2">
              {arquivos.map((msg) => (
                <MediaRow key={msg.id} msg={msg} icon={FileText} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="links" className="mt-3">
          {linksLoading ? (
            <div className="space-y-2">
              {[1, 2].map((i) => (
                <Skeleton key={i} className="h-10 w-full rounded-md" />
              ))}
            </div>
          ) : linkRows.length === 0 ? (
            <EmptyState icon={Link2} label={t('panel.noLinks')} />
          ) : (
            <div className="space-y-2">
              {linkRows.map(({ msg, url }) => (
                <a
                  key={msg.id}
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="block rounded-md border border-border/50 px-3 py-2 hover:bg-muted/50 transition-colors"
                >
                  <span className="text-xs text-primary truncate block">{url}</span>
                  <span className="text-[10px] text-muted-foreground truncate block">
                    {msg.conteudo}
                  </span>
                </a>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
